// @ts-nocheck
/**
 * Birch Registry — shared client SDK.
 * Exposed as `window.BirchRegistry` so other registry assets can use it
 * without ES-module import paths (Shopify CDN URLs are dynamic).
 *
 * Used by all parent-side registry pages and the public gift-giver page.
 */
(function () {
  const API_BASE = "/apps/registry/api";

  // ── Guest sessions ─────────────────────────────────────────────────────────
  //
  // A shopper who hasn't signed in can still build a registry. Their identity is
  // an httpOnly, HMAC-signed cookie the Registry worker mints at
  // POST /apps/registry/api/guest/session — the ONE mint point, so two
  // concurrent first-calls (the chat SSE and a registry list, say) can't race to
  // Set-Cookie and orphan a freshly built registry under a dead id.
  //
  // Because the cookie is httpOnly, page JS can't read it. We don't need to:
  // every registry call is same-origin so the browser attaches it automatically,
  // and all the client needs to know is "am I a guest", which Liquid already
  // tells us via body[data-reg-auth].
  //
  // The bootstrap is LAZY — nothing fires on a page load. It runs the first time
  // a guest actually performs a registry action, so a signed-out shopper reading
  // the marketing page costs zero extra requests.

  // "customer" | "guest" — stamped on the <body> TAG by layout/theme.liquid, so
  // it is present on every surface that loads this file, before this file parses.
  //
  // ⚠️ It used to be stamped only by registry-edit.liquid and registry-start.liquid,
  // while this store is also loaded on PDPs (blocks/buy-buttons.liquid) and on
  // registry-share / progress / manage / gifts / browse / public / giver. On
  // those pages the marker was absent, the fallback below read a signed-OUT
  // shopper as "customer", ensureGuestSession() no-opped, and every guest
  // registry action — including the AI builder, which asks this module who it is
  // — refused with "sign in required". Keep the marker theme-wide.
  //
  // Absent ⇒ assume customer, preserving pre-guest behaviour for any surface
  // rendered outside theme.liquid (e.g. an app/checkout layout).
  function authMode() {
    return document.body?.dataset?.regAuth === "guest" ? "guest" : "customer";
  }

  function isGuest() {
    return authMode() === "guest";
  }

  // Memoized so N parallel calls share ONE mint round trip. Kept as the promise
  // (not the result) so concurrent callers await the same in-flight request.
  let guestSessionPromise = null;

  // Readable breadcrumb for "this browser has (or had) a guest session".
  //
  // A signed-in page needs to know whether a claim is even worth attempting, or
  // every signed-in load pays a pointless round trip. This flag is a hint only:
  // it is never trusted for authorization (the signed token is), and if it's
  // missing the server's implicit claim on the registries-list read still adopts
  // the work. Cleared once a claim has been attempted.
  const GUEST_PENDING_KEY = "birch:guest_pending";

  // ── Guest token transport ──────────────────────────────────────────────────
  // ⚠️ Shopify's App Proxy strips `Cookie` from requests and `Set-Cookie` from
  // responses, by design — so the httpOnly cookie the Registry worker sets can
  // NEVER reach the browser through /apps/*. (Verified: the same mint returns
  // Set-Cookie when the worker is hit directly, and nothing through the proxy.)
  // The token therefore comes back in the mint's JSON body, lives here in
  // localStorage, and is replayed as `?gs=…` on every call from the single
  // request() choke point below. Query params survive the proxy and are covered
  // by Shopify's request signature.
  //
  // Mirrors GUEST_QUERY_PARAM in Workers/birch-registry/app/lib/guestSession.js
  // and Workers/birch-ai/apps/edge-worker/src/lib/guest-session.ts.
  const GUEST_TOKEN_KEY = "birch:grs";
  const GUEST_QUERY_PARAM = "gs";

  function markGuestPending() {
    try { localStorage.setItem(GUEST_PENDING_KEY, "1"); } catch (_) {}
  }
  function hasGuestPending() {
    try { return localStorage.getItem(GUEST_PENDING_KEY) === "1"; } catch (_) { return false; }
  }
  function clearGuestPending() {
    try { localStorage.removeItem(GUEST_PENDING_KEY); } catch (_) {}
  }
  function getGuestToken() {
    try { return localStorage.getItem(GUEST_TOKEN_KEY) || null; } catch (_) { return null; }
  }
  function setGuestToken(token) {
    try { if (token) localStorage.setItem(GUEST_TOKEN_KEY, token); } catch (_) {}
  }
  function clearGuestToken() {
    try { localStorage.removeItem(GUEST_TOKEN_KEY); } catch (_) {}
  }

  // ── Private-registry unlock token ──────────────────────────────────────────
  // Same story as the guest token above: the server's httpOnly unlock cookie is
  // stripped by the App Proxy, so a gift-giver who entered the CORRECT password
  // was re-shown the gate forever. The token now comes back in the unlock
  // response body, is stored per-slug, and is replayed as `?ut=…` on every
  // public-by-slug read.
  //
  // Scoped per slug (an HMAC over slug + passwordSetAt), so one registry's token
  // can never open another, and the owner rotating the password invalidates it.
  // Mirrors UNLOCK_QUERY_PARAM in app/lib/registryPassword.server.js.
  const UNLOCK_TOKEN_PREFIX = "birch:unlock:";
  const UNLOCK_QUERY_PARAM = "ut";

  function getUnlockToken(slug) {
    try { return localStorage.getItem(UNLOCK_TOKEN_PREFIX + slug) || null; } catch (_) { return null; }
  }
  function setUnlockToken(slug, token) {
    try { if (token) localStorage.setItem(UNLOCK_TOKEN_PREFIX + slug, token); } catch (_) {}
  }
  function clearUnlockToken(slug) {
    try { localStorage.removeItem(UNLOCK_TOKEN_PREFIX + slug); } catch (_) {}
  }

  /** Append this slug's unlock token, when we hold one. No-op for public registries. */
  function withUnlockToken(path, slug) {
    const token = getUnlockToken(slug);
    if (!token) return path;
    return path + (path.indexOf("?") === -1 ? "?" : "&")
      + UNLOCK_QUERY_PARAM + "=" + encodeURIComponent(token);
  }

  /**
   * Append the guest token to a storefront API path. Exported on the namespace
   * so the chat companion can carry the same identity on its /apps/ai/* calls —
   * one token covers both workers (same storefront origin).
   *
   * Deliberately NOT gated on isGuest(): this mirrors how the cookie used to
   * behave — sent on every request regardless of auth state — which is what lets
   * the server run the implicit claim when a shopper signs in mid-build. The
   * server always prefers a signed-in `logged_in_customer_id` over the token, so
   * a stale token on an authenticated request can never hijack ownership.
   */
  function withGuestToken(path) {
    const token = getGuestToken();
    if (!token) return path;
    return path + (path.indexOf("?") === -1 ? "?" : "&")
      + GUEST_QUERY_PARAM + "=" + encodeURIComponent(token);
  }

  /**
   * Ensure this browser has a guest session before a guest-mode registry call.
   * No-op (and no network) for a signed-in customer. Resolves to true when a
   * guest identity is in place, false when the server declined to issue one
   * (misconfiguration) — callers should surface the sign-in prompt then.
   */
  async function ensureGuestSession() {
    if (!isGuest()) return true;
    // Already holding a verified-by-the-server token — no round trip. The
    // server re-stamps it on the next mint if it ever ages toward the ceiling.
    if (getGuestToken()) return true;
    if (!guestSessionPromise) {
      // Send any token we DO have so the server refreshes that identity rather
      // than minting a second one (idempotent by design).
      guestSessionPromise = fetch(`${API_BASE}${withGuestToken("/guest/session")}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const ok = !!(d && d.guest);
          if (ok) {
            // The token comes back in the BODY — Set-Cookie can't survive the
            // App Proxy. Storing it is what makes the identity durable.
            setGuestToken(d.token);
            markGuestPending();
          }
          return ok;
        })
        // A failed mint must not poison the memo — the next action retries.
        .catch(() => { guestSessionPromise = null; return false; });
    }
    return guestSessionPromise;
  }

  /**
   * Attach anything built as a guest to the customer who just signed in.
   *
   * The server ALSO claims implicitly on the registries-list read, so this is
   * belt-and-braces plus a way to learn WHICH registry was adopted (so the
   * builder can navigate straight to it). Cheap and safe to call on any
   * authenticated registry page: with no guest token present it short-circuits
   * server-side before touching the database.
   *
   * Returns the claimed registry summaries (`[]` when there was nothing to claim).
   */
  async function claimGuestWork(identity = {}) {
    if (isGuest()) return [];
    // Skip the round trip entirely when this browser never held a guest session.
    // A false negative (localStorage cleared between building and signing in)
    // costs nothing: the server still claims implicitly on the registries-list
    // read — we just don't get to navigate straight to the adopted registry.
    if (!hasGuestPending() && !getGuestToken()) return [];
    // Clear up front so a failure can't leave us retrying on every page load.
    clearGuestPending();
    try {
      // withGuestToken is what tells the server WHICH guest to claim — the
      // token is the only identity link now that the cookie can't survive.
      const res = await fetch(`${API_BASE}${withGuestToken("/guest/claim")}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          registrantFirst: identity.registrantFirst || "",
          registrantLast: identity.registrantLast || "",
          customerEmail: identity.customerEmail || customerEmailHint(),
        }),
      });
      if (!res.ok) return [];
      const data = await res.json();
      // The work now belongs to the account — drop the token so we stop
      // presenting an identity that owns nothing (the server-side equivalent is
      // clearGuestCookieHeader, which the proxy also strips).
      clearGuestToken();
      return Array.isArray(data?.claimed) ? data.claimed : [];
    } catch (_) {
      return [];
    }
  }

  /**
   * Where to send a guest to sign in, returning them to where they were.
   * `return_to` keeps the query string (so ?id=<registry> survives) — and the
   * builder ALSO re-resolves the most-recently-updated registry on arrival, so
   * the landing is correct even if Shopify drops the nested query.
   */
  function signInUrl(returnTo) {
    const target = returnTo || (location.pathname + (location.search || ""));
    return `/customer_authentication/login?return_to=${encodeURIComponent(target)}`;
  }

  async function request(path, init = {}, _retried = false) {
    // Guest-mode calls need an identity before they can be authorized. This is
    // the single choke point every registry API call already goes through, so
    // one await here covers the whole surface — and it's a no-op after the
    // first call (memoized) and for signed-in customers (returns immediately).
    if (isGuest()) await ensureGuestSession();
    // Shopify App Proxy only forwards GET/POST — tunnel PATCH/DELETE via override header.
    const method = (init.method || "GET").toUpperCase();
    const needsOverride = method === "PATCH" || method === "DELETE";
    // The guest token rides the query string — the proxy strips cookies, so this
    // is the ONLY thing that carries guest identity. Appended here, at the one
    // choke point, so every existing caller gets it for free.
    const res = await fetch(`${API_BASE}${withGuestToken(path)}`, {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(needsOverride ? { "X-HTTP-Method-Override": method } : {}),
        ...(init.headers || {}),
      },
      ...init,
      method: needsOverride ? "POST" : method,
    });
    let data = null;
    try { data = await res.json(); } catch (_) {}
    if (!res.ok) {
      // A stored guest token that has aged past the server's 60-day ceiling (or
      // was signed with a rotated secret) fails verification forever, and
      // ensureGuestSession() would short-circuit on its mere presence. Drop it
      // and re-mint ONCE, so a stale token self-heals instead of bricking the
      // builder for that browser.
      if (res.status === 401 && data && data.error === "sign_in_required"
          && isGuest() && getGuestToken() && !_retried) {
        clearGuestToken();
        guestSessionPromise = null;
        return request(path, init, true);
      }
      const err = new Error((data && data.error) || `http_${res.status}`);
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  }

  // Best-effort signed-in customer email for the REGISTRY_CREATED milestone.
  // Sourced from the builder form (Liquid-rendered) or an optional global other
  // surfaces can set; "" when unknown (the server then just skips the event).
  function customerEmailHint() {
    try {
      return (
        document.getElementById("registry-create-form")?.dataset.customerEmail ||
        window.__birchCustomerEmail ||
        ""
      );
    } catch (_) { return ""; }
  }

  // ── Active-registry flag ───────────────────────────────────────────────────
  // A cached "does this shopper have ≥1 active (non-ended) registry?" boolean so
  // surfaces like the product-page "Add to registry" button can decide their
  // visibility WITHOUT a blocking network call on every page load. The flag is
  // kept fresh as a side effect of the /registries list fetch and by the
  // create/end/remove mutations below. A `birch:registry-flag` event fires on
  // every change so listeners (registry-product.js) can re-render immediately.
  const ACTIVE_FLAG_KEY = "birch:has_active_registry";

  // null = unknown (never fetched on this browser); true/false = last known.
  function hasActiveRegistryCached() {
    try {
      const v = localStorage.getItem(ACTIVE_FLAG_KEY);
      return v === null ? null : v === "1";
    } catch (_) { return null; }
  }

  function setActiveRegistryFlag(has) {
    const next = has ? "1" : "0";
    let changed = true;
    try {
      changed = localStorage.getItem(ACTIVE_FLAG_KEY) !== next;
      localStorage.setItem(ACTIVE_FLAG_KEY, next);
    } catch (_) { /* private mode / storage disabled — event still fires */ }
    if (changed) {
      try {
        document.dispatchEvent(
          new CustomEvent("birch:registry-flag", { detail: { hasActive: !!has } })
        );
      } catch (_) {}
    }
  }

  // Recompute the flag from a registries array (as returned by API.list()).
  function syncActiveRegistryFlag(registries) {
    const hasActive =
      Array.isArray(registries) && registries.some((r) => r && r.status !== "ended");
    setActiveRegistryFlag(hasActive);
    return hasActive;
  }

  const RegistryAPI = {
    // list() doubles as the flag's refresh point — any caller keeps the cached
    // "has active registry" boolean current for free.
    list: async () => {
      const data = await request("/registries");
      syncActiveRegistryFlag(data && data.registries);
      return data;
    },
    get: (id) => request(`/registries/${id}`),
    // A successful create always yields a fresh active registry.
    create: async (data) => {
      const res = await request("/registries", { method: "POST", body: JSON.stringify(data) });
      setActiveRegistryFlag(true);
      return res;
    },
    update: (id, patch) => request(`/registries/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    // Set/replace the gate password for a private registry (hashed server-side;
    // the hash is never sent or returned). Pass "" / call clearRegistryPassword
    // to remove it. Separate from update() so a raw hash can never ride a PATCH.
    setRegistryPassword: (id, password) =>
      request(`/registries/${id}/password`, { method: "POST", body: JSON.stringify({ password }) }),
    clearRegistryPassword: (id) =>
      request(`/registries/${id}/password`, { method: "DELETE" }),
    publish: (id) => request(`/registries/${id}/publish`, { method: "POST" }),
    // end/remove change the active count — but the shopper may still have other
    // live registries, so re-derive the flag from the full list (list() syncs it).
    end: async (id) => {
      const res = await request(`/registries/${id}/end`, { method: "POST" });
      try { await RegistryAPI.list(); } catch (_) {}
      return res;
    },
    remove: async (id) => {
      const res = await request(`/registries/${id}`, { method: "DELETE" });
      try { await RegistryAPI.list(); } catch (_) {}
      return res;
    },
    // CUR-167: a guest holds one registry, so "+ New registry" clears it.
    // Guests only (403 when signed in). Resolves { registry: <fresh> };
    // 409 has_gifts when anything was bought from it.
    startOver: (id) => request(`/registries/${id}/start-over`, { method: "POST" }),
    addItem: (registryId, item) =>
      request(`/registries/${registryId}/items`, {
        method: "POST",
        // Attach the signed-in customer's email (exposed on the builder form) so
        // the server can fire the REGISTRY_CREATED milestone on the 0→1 item
        // transition. Harmless on later adds — the server only uses it when the
        // registry was previously empty. Caller-supplied email wins.
        body: JSON.stringify({ customerEmail: customerEmailHint(), ...item }),
      }),
    updateItem: (registryId, itemId, patch) =>
      request(`/registries/${registryId}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(patch) }),
    // Product-detail popup for an item ALREADY on the registry — keyed by
    // itemId, so it includes the item's own stored description/notes (e.g.
    // the "Description" a shopper typed in the extension's or web's add-item
    // review form), not just catalog/AI enrichment. Use this over the
    // descriptor-based /product-detail route whenever an itemId is known.
    itemDetails: (itemId) =>
      request(`/items/${itemId}/details`),
    // Variant switcher: list a Shopify item's product variants so the registrant
    // can change size/color. Apply with updateItem(registryId, itemId,
    // { shopifyVariantId }). Returns { options, variants, selectedVariantId }.
    itemVariants: (registryId, itemId) =>
      request(`/registries/${registryId}/items/${itemId}/variants`),
    // Same variant list, for a BROWSE card that isn't a registry item yet (the
    // intake walkthrough, browse grid, alternates rows). `descriptor` is
    // { source, handle|productId } for Birch or { source, lushlifeId,
    // externalUrl, retailer } for LushLife. Shape-identical to itemVariants,
    // with selectedVariantId always null — nothing is bound yet.
    productVariants: (descriptor) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(descriptor || {})) {
        if (v != null && v !== "") p.set(k, String(v));
      }
      return request(`/product-variants?${p.toString()}`);
    },
    removeItem: (registryId, itemId) =>
      request(`/registries/${registryId}/items/${itemId}`, { method: "DELETE" }),
    // ── Review Box (AI suggestions) ──────────────────────────────────────────
    // Keep a pending suggestion → flips it to "active" (live). The server only
    // permits the suggested→active / suggested→removed transitions.
    keepSuggestion: (registryId, itemId) =>
      request(`/registries/${registryId}/items/${itemId}`, { method: "PATCH", body: JSON.stringify({ status: "active" }) }),
    // Dismiss a pending suggestion → removed. (DELETE also works for suggested
    // items; this is the explicit status form.)
    dismissSuggestion: (registryId, itemId) =>
      request(`/registries/${registryId}/items/${itemId}`, { method: "PATCH", body: JSON.stringify({ status: "removed" }) }),
    // Bulk Keep-all / Dismiss-all. Scope with { batchId } (one build) or
    // { itemIds } (a specific set of cards, e.g. one category section). Both are
    // ONE atomic server-side updateMany — idempotent and retry-safe, so a
    // partial-failure can never leave the UI mid-flip the way a per-item
    // PATCH fan-out could.
    approveSuggestions: (registryId, opts = {}) =>
      request(`/registries/${registryId}/suggestions/approve`, { method: "POST", body: JSON.stringify({ batchId: opts.batchId || null, itemIds: opts.itemIds || null }) }),
    rejectSuggestions: (registryId, opts = {}) =>
      request(`/registries/${registryId}/suggestions/reject`, { method: "POST", body: JSON.stringify({ batchId: opts.batchId || null, itemIds: opts.itemIds || null }) }),
    scrapeUrl: (url) =>
      request("/scrape-url", { method: "POST", body: JSON.stringify({ url }) }),
    // Copy-registry: preview the items a pasted registry URL would import.
    // Birch URL → reads our DB (flags out-of-stock); external URL (Babylist /
    // Poppylist / Zola / …) → scrapes + matches to Birch products. Returns
    // { sourceType, sourceName, provider, items:[{key,matchType,title,imageUrl,
    //   priceCents,inStock,unavailable,retailer,add}], truncated, error? }.
    // Pass the current registryId (when one exists) so the preview can flag
    // items already on it — they're shown as "Already added" and skipped.
    importPreview: (url, registryId) =>
      request("/import/preview", { method: "POST", body: JSON.stringify({ url, registryId: registryId || null }) }),
    // Commit the user-selected items onto a registry. `data` =
    // { registryId?, items:[<add payload>], sourceName? }. Creates a registry
    // when registryId is omitted. Returns { registryId, slug, added, merged }.
    importCommit: (data) =>
      request("/import/commit", { method: "POST", body: JSON.stringify(data) }),
    uploadImage: async (file) => {
      // multipart upload — don't set Content-Type, fetch will add the boundary.
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/upload-image`, {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      let data = null;
      try { data = await res.json(); } catch (_) {}
      if (!res.ok) {
        const err = new Error((data && data.error) || `http_${res.status}`);
        err.status = res.status;
        err.body = data;
        throw err;
      }
      return data; // { url, key }
    },
    publicView: async (slug) => {
      try {
        return await request(withUnlockToken(`/public/${slug}`, slug));
      } catch (e) {
        // A stored token that no longer verifies — the owner rotated (or
        // cleared) the password, which re-stamps passwordSetAt and invalidates
        // every issued token. Drop it rather than replaying a dead credential on
        // every load; the caller re-renders the password gate.
        if (e && e.status === 401 && e.body && e.body.gated) clearUnlockToken(slug);
        throw e;
      }
    },
    // Private (password-gated) registries: submit the owner's shared password.
    // On success the server returns a signed unlock token scoped to the slug,
    // which we store so subsequent public-by-slug reads present it. Throws (401)
    // with err.body.error === "invalid_password" on mismatch.
    //
    // The token comes back in the BODY, not a cookie: Shopify's App Proxy strips
    // Set-Cookie, so the cookie the server also sets never reaches the browser.
    // Without storing this, a correct password re-rendered the gate forever.
    unlockRegistry: async (slug, password) => {
      const data = await request(`/public/${slug}/unlock`, {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      if (data && data.token) setUnlockToken(slug, data.token);
      return data;
    },
    // Fire a single view event per browser. Reloads, back/forward, and new
    // tabs are deduped by a localStorage marker keyed by slug. Server-side
    // dedupe could be added later (cookie + DB unique index), but this
    // already prevents the most common owner complaint ("my number ticks up
    // every time I refresh"). Idempotent + safe to call on every load.
    recordView: async (slug, channel) => {
      if (!slug) return { skipped: true };
      const lsKey = "birchRegistryViewed:" + slug;
      try {
        if (localStorage.getItem(lsKey)) return { skipped: true };
      } catch (_) { /* private mode — just record and move on */ }
      try {
        await request(withUnlockToken(`/public/${slug}/view`, slug), {
          method: "POST",
          body: JSON.stringify({ channel: channel || null }),
        });
        try { localStorage.setItem(lsKey, String(Date.now())); } catch (_) {}
        return { recorded: true };
      } catch (_) {
        return { error: true };
      }
    },
    // markPurchased was the legacy "I bought it outside Birch" self-report
    // helper. External items now go through the unified gift cart (giftCartUI →
    // API.checkoutCart), checked out together with own-store items, so the
    // helper is removed and no theme code calls the old route.
    // Group giving — pool endpoints
    poolSummary: (poolId) => request(`/pool/${poolId}/summary`),
    poolContribute: (poolId, data) => request(`/pool/${poolId}/contribute`, { method: "POST", body: JSON.stringify(data) }),
    enableGroupGift: (registryId, itemId) => request(`/registries/${registryId}/items/${itemId}/group-gift/enable`, { method: "POST" }),
    disableGroupGift: (registryId, itemId) => request(`/registries/${registryId}/items/${itemId}/group-gift/disable`, { method: "POST" }),
    redeemPool: (poolId, data) => request(`/pool/${poolId}/redeem`, { method: "POST", body: JSON.stringify(data) }),
    createCheckout: (data) => request("/public/create-checkout", { method: "POST", body: JSON.stringify(data) }),
    // Unified gift-cart checkout. `data` = { items:[{itemId,quantity}],
    // firstName, lastName, email, phone, message, giftReceipt, shipTo }.
    // Builds ONE Shopify draft order mixing own-store + external lines and
    // returns { ok, invoiceUrl }. See proxy.api.checkout-cart.tsx.
    checkoutCart: (data) => request("/checkout-cart", { method: "POST", body: JSON.stringify(data) }),
    // Manually release a held gift so it ships now. Required for manual_release
    // registries (parent releases each gift) and for early release on
    // ship_on_date registries. Backend: POST /apps/registry/api/purchases/:id/release
    // → { ok, purchaseId, holdStatus }. Idempotent (already-released is a no-op).
    releasePurchase: (purchaseId) =>
      request(`/purchases/${purchaseId}/release`, { method: "POST" }),
    // CUR-158 — per-gift ship controls. body is { shipOn: "yyyy-mm-dd" } (ship
    // that gift on that day) or { hold: true } (keep it until Ship now).
    // Backend: POST /apps/registry/api/purchases/:id/schedule
    // → { ok, affected: [purchaseId…], scheduledShipAt }. Gifts that share one
    // Shopify shipment move together; `affected` lists all of them.
    schedulePurchase: (purchaseId, body) =>
      request(`/purchases/${purchaseId}/schedule`, { method: "POST", body: JSON.stringify(body) }),
    // Same go-ahead for an agent-purchased (external) gift on a delayed
    // registry. Backend: POST /apps/registry/api/external-orders/:id/release
    // → { ok, id, releasedAt }. Idempotent.
    releaseExternalOrder: (externalOrderId) =>
      request(`/external-orders/${externalOrderId}/release`, { method: "POST" }),
    // Free-text thank-you note to the gift-giver (CUR-50). Available as soon
    // as the gift is purchased — no delivery-status gate. Send-once: a repeat
    // call against an already-thanked purchase is a no-op on the backend
    // (returns { ok, alreadySent: true }, no re-send). Backend:
    // POST /apps/registry/api/purchases/:id/thank-you { note } → { ok, sentAt, emailed }.
    sendThankYou: (purchaseId, note) =>
      request(`/purchases/${purchaseId}/thank-you`, { method: "POST", body: JSON.stringify({ note }) }),
    // The other two kinds of giver. Same body ({ note }), same named outcomes
    // (status: sent | partial | already_sent | no_recipients | send_failed) —
    // three routes only because the three gift kinds are three different rows.
    //
    // DISJOINT is why sendThankYou() above cannot serve them: an external gift
    // never gets a RegistryPurchase and neither does a pool contribution, so
    // the purchases route is keyed on a row that does not exist for either.
    //
    // Thank the giver of an agent-bought (external) gift.
    // POST /apps/registry/api/external-orders/:id/thank-you { note }
    thankExternalOrder: (externalOrderId, note) =>
      request(`/external-orders/${externalOrderId}/thank-you`, { method: "POST", body: JSON.stringify({ note }) }),
    // Thank ONE group-gift contributor. `contributionId` may be any of that
    // person's contribution rows: the server folds them on the lowercased
    // contributor email and claims their earliest completed row, then reports
    // every row it stamped in `stampedIds`.
    // POST /apps/registry/api/contributions/:id/thank-you { note }
    thankContribution: (contributionId, note) =>
      request(`/contributions/${contributionId}/thank-you`, { method: "POST", body: JSON.stringify({ note }) }),
    // Thank every contributor to one item's pool in a single click. Anyone
    // already thanked is SKIPPED, not re-mailed (skippedCount — not a
    // failure), and a contributor whose send fails has their claim released,
    // so clicking again reaches exactly the stragglers.
    // POST /apps/registry/api/items/:itemId/thank-all { note }
    thankAllContributors: (itemId, note) =>
      request(`/items/${itemId}/thank-all`, { method: "POST", body: JSON.stringify({ note }) }),
    // FUTURE: external-order returns. Endpoint already exists at
    //   POST /apps/registry/api/external-orders/:id/return  (body: { reason })
    // — implemented in Workers/birch-registry/app/routes/proxy.api.external-orders.$id.return.tsx
    // Only allowed while order.status ∈ {shipped, delivered}; refund posts
    // as Birch credit for retailerPriceCents (3% markup is non-refundable
    // per PLAN.md § 7.7). Add an `requestExternalReturn(id, reason)` helper
    // here when the registry-progress page is wired to surface them.
    // Bank / wallet endpoints.
    bank: {
      // Cancel a still-held catalog gift and convert it to Birch credit
      // (less any reward credit already earned on this purchase). Only
      // works while holdStatus ∈ {held, scheduled} and item is Birch-source.
      // Backend lifecycle is tracked in RegistryReturnRequest (status moves
      // pending → completed atomically in this call). Idempotent on repeats:
      // a second call while one is pending returns the existing request.
      cancelToBank: (purchaseId) =>
        request(`/bank/add-to-bank`, { method: "POST", body: JSON.stringify({ purchaseId }) }),
    },
  };

  /**
   * Get-or-create the customer's registry — the SINGLE client-side entry point
   * for "make sure a registry exists, then give me it." Shared by every
   * storefront surface that can run before the customer has one (the product-page
   * "Add to registry" button and the registry builder/edit page), so the
   * create behaviour lives in one place.
   *
   * Returns the most-recently-updated ACTIVE registry if any, else creates a new
   * one. The name is intentionally NOT sent — the server fills it from the shared
   * buildDefaultRegistryName() default so naming is defined once, server-side.
   * Pass the signed-in customer's identity (from Liquid) so the new registry is
   * attributed correctly.
   *
   * Returns { registry, created }.
   */
  async function getOrCreateRegistry({ registrantFirst, registrantLast, customerEmail, forceCreate } = {}) {
    // forceCreate (multi-registry #8): skip reusing an existing active registry
    // and always create a fresh one — used by "+ Start a new registry".
    if (!forceCreate) {
      const { registries } = await RegistryAPI.list();
      const active = (registries || [])
        .filter((r) => r.status !== "ended")
        .slice()
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      if (active[0]) return { registry: active[0], created: false };
    }

    const created = await RegistryAPI.create({
      eventType: "baby_shower",
      registrantFirst: registrantFirst || "",
      registrantLast: registrantLast || "",
      customerEmail: customerEmail || "",
    });
    return { registry: created.registry, created: true };
  }

  /**
   * Adds a Birch (Shopify) item to the cart with registry tracking properties.
   * The order webhook later reads these properties to auto-mark purchased.
   */
  async function addBirchItemToCart({ variantId, quantity = 1, registryId, registryItemId, buyerName, buyerEmail, giftNote, contributionCents }) {
    // Shopify line-item-property convention: keys prefixed with `_` are hidden
    // from the cart/checkout display. Use it for internal tracking IDs so the
    // buyer doesn't see opaque cuids next to their gift.
    const properties = {};
    if (registryId)     properties._registry_id      = registryId;
    if (registryItemId) properties._registry_item_id = registryItemId;
    if (buyerName)      properties["From"]           = buyerName;
    if (buyerEmail)     properties._gift_email       = buyerEmail;
    if (giftNote)       properties["Gift note"]      = giftNote;
    if (contributionCents) {
      properties._gift_contribution_cents = String(contributionCents);
      properties._gift_type = "group_contribution";
    }

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) throw new Error("cart_add_failed");

    if (registryId) {
      // Cart attributes are also hidden when the key starts with `_`.
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            _registry_id: registryId,
            _registry_item_id: registryItemId || "",
          },
        }),
      });
    }
    return res.json();
  }

  function showToast(message, ms = 2400) {
    const el = document.createElement("div");
    el.className = "registry-toast";
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), ms);
  }

  function escapeHtml(s) {
    return String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatMoney(cents, currency = "USD") {
    if (cents == null) return "";
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  }

  function formatDate(s) {
    try {
      return new Date(s).toLocaleDateString(undefined, {
        month: "long", day: "numeric", year: "numeric",
      });
    } catch (_) { return s; }
  }

  // Group-gift pools that aren't full but take no more money (the worker's
  // shapePool status — checkout refuses both, so the giver pages show this
  // instead of a Contribute button). "resolving" = the family is paying a
  // top-up / redirect invoice; it reopens by itself if they abandon it.
  // "closed" = converted to Bank credit. Anything else → "" (open/funded).
  const POOL_LOCK_NOTES = {
    resolving: "The family is finishing up this gift — contributions are paused for now.",
    closed: "This gift is closed to contributions — thank you to everyone who chipped in!",
  };
  function poolLockNote(status) {
    return POOL_LOCK_NOTES[status] || "";
  }

  const STAGES = [
    { id: "before_baby",   label: "Before Baby" },
    { id: "first_week",    label: "First Week Home" },
    { id: "sleep",         label: "Sleep & Nursery" },
    { id: "feeding",       label: "Feeding" },
    { id: "on_the_go",     label: "On the Go" },
    { id: "bath",          label: "Bath & Care" },
    { id: "play",          label: "Play & Learn" },
    { id: "later",         label: "3 to 6 Months" },
    { id: "parent_recovery", label: "Parent Recovery" },
    { id: "uncategorized", label: "Other" },
  ];

  // Keyword → stage classifier. Order matters: first match wins, so put
  // unambiguous terms (e.g. "bassinet") in the right bucket and leave broad
  // generic terms ("baby") off the list.
  // Match is whole-word, case-insensitive, against title + handle + tags.
  const STAGE_KEYWORDS = {
    sleep: [
      "bassinet", "crib", "cradle", "co-sleeper", "co sleeper", "moses basket",
      "sleep sack", "sleep bag", "swaddle", "swaddling",
      "sheet", "mattress", "mattress pad", "crib sheet", "fitted sheet",
      "monitor", "baby monitor", "video monitor", "audio monitor",
      "nightlight", "night light", "white noise", "sound machine",
      "nursery", "mobile", "blackout", "rocker", "glider",
    ],
    feeding: [
      "bottle", "bottles", "nipple", "teat",
      "formula", "milk powder", "breastfeed", "breastfeeding",
      "breast pump", "pump", "lactation", "nursing pillow",
      "sterilizer", "steriliser", "bottle warmer",
      "bib", "burp cloth", "burp rag",
      "high chair", "highchair", "booster seat",
      "feeding", "weaning", "baby food", "spoon", "sippy cup", "training cup",
      "hipp", "kendamil", "holle", "loulouka",
    ],
    on_the_go: [
      "stroller", "pram", "buggy", "pushchair",
      "car seat", "carseat", "infant seat", "convertible seat",
      "carrier", "wrap", "sling", "babywearing",
      "diaper bag", "nappy bag", "changing bag",
      "travel system", "stroller frame",
    ],
    bath: [
      "bath", "bathtub", "tub",
      "towel", "hooded towel", "washcloth", "wash cloth",
      "shampoo", "body wash", "bubble bath", "soap",
      "lotion", "balm", "diaper cream", "barrier cream", "rash cream",
      "diaper", "diapers", "nappy", "nappies", "wipe", "wipes",
      "changing pad", "changing mat", "diaper pail",
    ],
    play: [
      "toy", "toys", "rattle", "teether", "play mat", "playmat",
      "activity gym", "play gym", "tummy time",
      "book", "board book", "stuffed animal", "lovey", "plush",
      "blocks", "puzzle", "musical",
    ],
    before_baby: [
      "maternity", "prenatal", "pregnancy",
      "hospital bag", "going home outfit",
      "vitamins", "prenatal vitamin",
      "belly band", "support belt",
    ],
    first_week: [
      "newborn", "0-3 month", "0-3 months", "swaddle blanket", "receiving blanket",
      "footed pajama", "footie", "onesie", "kimono top",
    ],
    later: [
      "walker", "jumper", "exersaucer", "bouncer",
      "high chair tray",
      "6 month", "6-12 month", "3-6 month", "first foods",
    ],
    parent_recovery: [
      "postpartum", "post partum", "fourth trimester", "perineal", "peri bottle",
      "sitz bath", "nursing pads", "nursing bra", "nursing pajama", "nursing pj",
      "recovery kit", "padsicle", "belly wrap", "postnatal", "lactation snack",
      "hydration", "mom recovery", "labor and delivery",
    ],
  };

  function classifyStage({ title = "", handle = "", tags = "" } = {}) {
    const haystack = `${title} ${handle.replace(/-/g, " ")} ${tags}`.toLowerCase();
    for (const [stage, keywords] of Object.entries(STAGE_KEYWORDS)) {
      for (const kw of keywords) {
        // Whole-word match using word boundaries (and a non-letter check at edges
        // so multi-word phrases like "car seat" still hit).
        const re = new RegExp(`(^|[^a-z0-9])${kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![a-z0-9])`, "i");
        if (re.test(haystack)) return stage;
      }
    }
    return "uncategorized";
  }

  const PRIORITIES = [
    { id: "must_have",    label: "Must have" },
    { id: "nice_to_have", label: "Nice to have" },
    { id: "optional",     label: "Optional" },
  ];

  // Persist the gift-giver's name + email locally so repeat visits to the
  // same registry (or a different one) don't have to retype it. Customer-
  // profile values from Liquid still win on the first paint; subsequent
  // sessions fall back to this cache.
  const GIFTER_KEY = "birchGifterIdentity";
  function getCachedGifter() {
    try {
      const raw = localStorage.getItem(GIFTER_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return {
        name: typeof parsed.name === "string" ? parsed.name : "",
        email: typeof parsed.email === "string" ? parsed.email : "",
      };
    } catch (_) { return null; }
  }
  function saveCachedGifter({ name, email }) {
    try {
      localStorage.setItem(GIFTER_KEY, JSON.stringify({
        name: (name || "").trim(),
        email: (email || "").trim(),
        savedAt: Date.now(),
      }));
    } catch (_) { /* private mode */ }
  }

  /**
   * Generic "gift-giver" modal: name / email / optional note.
   * Returns Promise<{name,email,note}|null>.
   *
   * Identity prefill priority:
   *   1. opts.defaultName / opts.defaultEmail (caller-provided — e.g. logged-in
   *      Shopify customer profile from Liquid).
   *   2. localStorage cache from a previous gift.
   * Submissions persist to the cache for next time.
   */
  function openGiverModal(opts = {}) {
    const cached = getCachedGifter() || {};
    const defaultName  = (opts.defaultName  ?? cached.name  ?? "").toString();
    const defaultEmail = (opts.defaultEmail ?? cached.email ?? "").toString();

    return new Promise((resolve) => {
      const backdrop = document.createElement("div");
      backdrop.className = "registry-modal-backdrop";
      backdrop.innerHTML = `
        <form class="registry-modal" novalidate>
          ${opts.icon ? `<div class="registry-modal__icon">${opts.icon}</div>` : ""}
          <h2>${escapeHtml(opts.title || "Send this gift")}</h2>
          ${opts.subtitle ? `<p class="registry-modal__subtitle">${escapeHtml(opts.subtitle)}</p>` : ""}
          ${opts.note ? `<p style="color:var(--reg-muted);margin-bottom:16px">${escapeHtml(opts.note)}</p>` : ""}
          <div class="registry-modal__field">
            <label for="reg-giver-name">Your name</label>
            <input id="reg-giver-name" name="name" required autocomplete="name" value="${escapeHtml(defaultName)}">
          </div>
          <div class="registry-modal__field">
            <label for="reg-giver-email">Email</label>
            <input id="reg-giver-email" name="email" type="email" required autocomplete="email" value="${escapeHtml(defaultEmail)}">
          </div>
          <div class="registry-modal__field">
            <label for="reg-giver-note">Gift note (optional)</label>
            <textarea id="reg-giver-note" name="note" rows="3"></textarea>
          </div>
          <div class="registry-modal__actions">
            <button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>
            <button type="submit" class="registry-btn registry-btn--primary">${escapeHtml(opts.submitLabel || "Continue")}</button>
          </div>
        </form>`;
      document.body.appendChild(backdrop);
      const form = backdrop.querySelector("form");
      backdrop.querySelector('[data-action="cancel"]').addEventListener("click", () => {
        backdrop.remove(); resolve(null);
      });
      // Focus the first empty input so prefilled identity flows straight to
      // the note field instead of forcing the user to skip past name/email.
      const nameInput  = form.querySelector("[name=name]");
      const emailInput = form.querySelector("[name=email]");
      const noteInput  = form.querySelector("[name=note]");
      const firstEmpty = !nameInput.value ? nameInput : !emailInput.value ? emailInput : noteInput;
      setTimeout(() => firstEmpty?.focus(), 0);

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const data = new FormData(form);
        const name = (data.get("name") || "").toString().trim();
        const email = (data.get("email") || "").toString().trim();
        if (!name || !email) return;
        saveCachedGifter({ name, email });
        backdrop.remove();
        resolve({
          name,
          email,
          note: (data.get("note") || "").toString().trim() || null,
        });
      });
    });
  }

  /**
   * Group-gift "amount" modal — shared by every gift-giver surface. Collects
   * ONLY the contribution amount (identity is collected once at cart checkout),
   * previews the 3% fee for external-store pools, and caps at the pool's
   * remaining balance. Resolves to the chosen amount in CENTS (toward the goal),
   * or null if cancelled.
   *
   * opts: { itemTitle, itemType ("native"|"external"), feeRate, remaining (dollars), buyAll }
   */
  const CONTRIB_FEE_RATE = 0.03;
  function openContributionAmountModal(opts = {}) {
    const itemTitle = opts.itemTitle || "this gift";
    const itemType = opts.itemType || "native";
    const feeRate = opts.feeRate != null ? opts.feeRate : (SERVICE_FEE_ENABLED && itemType === "external" ? CONTRIB_FEE_RATE : 0);
    const remaining = Number(opts.remaining) || 0;
    const buyAll = !!opts.buyAll;
    const remainingCents = Math.round(remaining * 100);
    const showFee = itemType === "external" && feeRate > 0;

    return new Promise((resolve) => {
      // Quick-amount chips: a few presets that fit under the remaining balance,
      // plus a "Whole gift" chip that covers the entire remaining (this replaces
      // the separate "Buy Entire Gift" card button — one entry point, all the
      // options live here). `buyAll` simply pre-selects the whole-gift amount.
      const presetDollars = [25, 50, 100].filter((d) => d * 100 < remainingCents);
      const defaultDollars = buyAll && remaining > 0
        ? remaining
        : Math.min(50, remaining > 0 ? remaining : 50);
      const chipsHtml = remainingCents > 0 ? `
          <div class="rgl-contrib-chips" role="group" aria-label="Quick amounts">
            ${presetDollars.map((d) => `<button type="button" class="rgl-contrib-chip" data-amt="${d}">$${d}</button>`).join("")}
            <button type="button" class="rgl-contrib-chip rgl-contrib-chip--full" data-amt="${(remainingCents / 100).toFixed(2)}">
              Whole gift · ${formatMoney(remainingCents)}
            </button>
          </div>` : "";

      const backdrop = document.createElement("div");
      backdrop.className = "registry-modal-backdrop";
      backdrop.innerHTML = `
        <form class="registry-modal rgl-contrib-modal" novalidate>
          <h2>Contribute to Gift</h2>
          <p class="registry-modal__subtitle">${escapeHtml(itemTitle)}</p>
          <div class="registry-modal__field">
            <label for="contrib-amount">Your contribution (USD)</label>
            <div class="rgl-amount-input-wrap">
              <span class="rgl-amount-input-prefix">$</span>
              <input id="contrib-amount" name="amount" type="number" min="1" step="1" value="${defaultDollars.toFixed(2)}">
            </div>
            ${remaining > 0 ? `<div class="registry-modal__hint">${formatMoney(remainingCents)} still needed</div>` : ""}
          </div>
          ${chipsHtml}
          ${showFee ? `
          <div class="rgl-fee-breakdown" id="contrib-fee-wrap">
            <div class="rgl-fee-breakdown__row"><span>Your contribution</span><span id="contrib-fee-base">—</span></div>
            <div class="rgl-fee-breakdown__row rgl-fee-breakdown__row--fee"><span>Service fee (3%)</span><span id="contrib-fee-amount">—</span></div>
            <div class="rgl-fee-breakdown__row rgl-fee-breakdown__row--total"><span>Total charged</span><span id="contrib-fee-total">—</span></div>
          </div>` : ""}
          <p class="registry-modal__fineprint">You'll add this to your gift cart and check out with your other gifts.</p>
          <div class="registry-modal__actions">
            <button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>
            <button type="submit" class="registry-btn registry-btn--primary">Add to gift cart</button>
          </div>
        </form>`;
      document.body.appendChild(backdrop);
      const form = backdrop.querySelector("form");
      const amountInput = form.querySelector("#contrib-amount");
      const chips = Array.from(form.querySelectorAll(".rgl-contrib-chip"));

      function syncActiveChip() {
        const cents = Math.round((parseFloat(amountInput.value) || 0) * 100);
        chips.forEach((c) => {
          const chipCents = Math.round(parseFloat(c.dataset.amt) * 100);
          c.classList.toggle("rgl-contrib-chip--active", chipCents === cents);
        });
      }
      function updateFee() {
        if (!showFee) return;
        const dollars = parseFloat(amountInput.value) || 0;
        const fee = Math.round(dollars * feeRate * 100) / 100;
        form.querySelector("#contrib-fee-base").textContent = formatMoney(Math.round(dollars * 100));
        form.querySelector("#contrib-fee-amount").textContent = formatMoney(Math.round(fee * 100));
        form.querySelector("#contrib-fee-total").textContent = formatMoney(Math.round((dollars + fee) * 100));
      }
      chips.forEach((c) =>
        c.addEventListener("click", () => {
          amountInput.value = parseFloat(c.dataset.amt).toFixed(2);
          updateFee();
          syncActiveChip();
        }));
      amountInput.addEventListener("input", () => { updateFee(); syncActiveChip(); });
      updateFee();
      syncActiveChip();

      const close = (val) => { backdrop.remove(); resolve(val); };
      backdrop.querySelector('[data-action="cancel"]').addEventListener("click", () => close(null));
      backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(null); });
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const dollars = parseFloat(amountInput.value);
        if (!Number.isFinite(dollars) || dollars < 1) { amountInput.focus(); return; }
        let cents = Math.round(dollars * 100);
        if (remainingCents > 0 && cents > remainingCents) cents = remainingCents;
        close(cents);
      });
    });
  }

  /**
   * Persisted gift cart, scoped to one registry slug. Holds line entries
   * { itemId, quantity, snapshot } where snapshot carries the display fields
   * (title, imageUrl, priceCents, currency, source, externalRetailer,
   * shippingCents) so the drawer renders without refetching. Survives reloads
   * via localStorage; emits change events to subscribers.
   *
   * Pricing note: own-store lines cost `priceCents`. External lines cost
   * `priceCents` + a 3% service fee + the listing's shipping — the same math
   * the server re-derives authoritatively at checkout (proxy.api.checkout-cart).
   */
  const EXTERNAL_FEE_RATE = 0.03;
  // Service-fee feature flag (mirror of the worker's SERVICE_FEE_ENABLED). When
  // false, no 3% fee is added to external items or external-pool contributions
  // and the cart's "Service fee" row + disclosure note self-hide (serviceFee = 0).
  const SERVICE_FEE_ENABLED = false;
  function giftCart(slug) {
    const KEY = "birchGiftCart:" + (slug || "default");
    const listeners = new Set();
    function load() {
      try {
        const raw = localStorage.getItem(KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr.filter((e) => e && e.itemId) : [];
      } catch (_) { return []; }
    }
    function persist(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (_) {}
      listeners.forEach((fn) => { try { fn(list); } catch (_) {} });
    }
    function lineTotalCents(entry) {
      const s = entry.snapshot || {};
      // Group-gift contribution: charged = chosen amount + (external) 3% fee.
      // Quantity is always 1; the figure the buyer puts in is the product amount.
      if (entry.kind === "contribution") {
        const product = entry.contributionCents || 0;
        const fee = SERVICE_FEE_ENABLED && s.feeRate ? Math.round(product * s.feeRate) : 0;
        return product + fee;
      }
      const qty = entry.quantity || 1;
      const base = s.priceCents || 0;
      if (s.source === "external") {
        const fee = SERVICE_FEE_ENABLED ? Math.round(base * EXTERNAL_FEE_RATE) : 0;
        const ship = s.shippingCents || 0;
        return (base + fee) * qty + ship * qty;
      }
      return base * qty;
    }
    return {
      items: load,
      get(itemId) { return load().find((e) => e.itemId === itemId) || null; },
      count() { return load().reduce((n, e) => n + (e.kind === "contribution" ? 1 : (e.quantity || 1)), 0); },
      has(itemId) { return load().some((e) => e.itemId === itemId); },
      subtotalCents() { return load().reduce((sum, e) => sum + lineTotalCents(e), 0); },
      // Physically-shipped external items only (drives the "shipping calculated
      // at checkout" copy). Contributions don't ship in this order.
      hasExternal() { return load().some((e) => e.kind !== "contribution" && (e.snapshot || {}).source === "external"); },
      // Any line carrying a 3% service fee — an external item OR a contribution
      // toward an external-store pool. Drives the fee disclosure copy.
      hasServiceFee() {
        if (!SERVICE_FEE_ENABLED) return false;
        return load().some((e) => {
          const s = e.snapshot || {};
          if (e.kind === "contribution") return (s.feeRate || 0) > 0;
          return s.source === "external";
        });
      },
      // Total 3% service fee across the cart (external item lines + external-pool
      // contributions), in cents. Already included in subtotalCents — surfaced
      // separately so the cart can show an explicit "Service fee (3%)" row.
      serviceFeeCents() {
        if (!SERVICE_FEE_ENABLED) return 0;
        return load().reduce((sum, e) => {
          const s = e.snapshot || {};
          if (e.kind === "contribution") {
            const product = e.contributionCents || 0;
            return sum + (s.feeRate ? Math.round(product * s.feeRate) : 0);
          }
          if (s.source === "external") {
            const base = s.priceCents || 0;
            return sum + Math.round(base * EXTERNAL_FEE_RATE) * (e.quantity || 1);
          }
          return sum;
        }, 0);
      },
      lineTotalCents,
      add(entry) {
        const list = load();
        const existing = list.find((e) => e.itemId === entry.itemId);
        if (existing) existing.quantity = (existing.quantity || 1) + (entry.quantity || 1);
        else list.push({ itemId: entry.itemId, quantity: entry.quantity || 1, snapshot: entry.snapshot || {} });
        persist(list);
      },
      // Add or replace a group-gift contribution line. One line per pool item:
      // re-adding the same item replaces the amount (it isn't a quantity).
      addContribution({ itemId, contributionCents, snapshot }) {
        const list = load();
        const e = list.find((x) => x.itemId === itemId);
        const cents = Math.max(0, Math.round(contributionCents || 0));
        if (e) {
          e.kind = "contribution";
          e.quantity = 1;
          e.contributionCents = cents;
          e.snapshot = { ...(e.snapshot || {}), ...(snapshot || {}) };
        } else {
          list.push({ itemId, quantity: 1, kind: "contribution", contributionCents: cents, snapshot: snapshot || {} });
        }
        persist(list);
      },
      setContributionCents(itemId, cents) {
        const list = load();
        const e = list.find((x) => x.itemId === itemId);
        if (!e || e.kind !== "contribution") return;
        const c = Math.max(0, Math.round(cents || 0));
        if (c <= 0) return this.remove(itemId);
        e.contributionCents = c;
        persist(list);
      },
      setQty(itemId, q) {
        const list = load();
        const e = list.find((x) => x.itemId === itemId);
        if (!e || e.kind === "contribution") return; // contributions edit amount, not qty
        if (q <= 0) return this.remove(itemId);
        e.quantity = q;
        persist(list);
      },
      remove(itemId) { persist(load().filter((e) => e.itemId !== itemId)); },
      clear() { persist([]); },
      onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    };
  }

  /**
   * Unified gift-cart UI — a slide-in drawer + floating button shared by every
   * gift-giver surface (registry-giver, registry-public). Both own-store and
   * external items add to the same cart and check out together in one Shopify
   * draft order via API.checkoutCart (server: proxy.api.checkout-cart).
   *
   * opts:
   *   slug            registry slug (cart is persisted per slug)
   *   getRegistry()   → the current registry object (name, registrantFirst,
   *                     deliverySetting) — read lazily so it can be set after
   *                     mount.
   *   getItem(id)     → the registry item object for an id (for snapshots).
   *   onChange()      → called after every cart mutation so the host page can
   *                     re-paint its grid ("In cart ✓" labels).
   *   defaultShipSelf() → optional bool; seeds the ship-to radio default.
   *
   * Returns { cart, addItem(item), open(), close(), refresh() }.
   */
  function giftCartUI(opts) {
    const cart = giftCart(opts.slug);
    const getRegistry = opts.getRegistry || (() => ({}));
    const onChange = opts.onChange || (() => {});
    const defaultShipSelf = opts.defaultShipSelf || (() => false);

    // Persist the in-progress gift note per slug so a paused message survives a
    // drawer close / reopen / reload. Cleared on a completed checkout.
    const NOTE_KEY = "birchGiftCartNote:" + (opts.slug || "default");
    function getCachedNote() {
      try { return localStorage.getItem(NOTE_KEY); } catch (_) { return null; }
    }
    function saveCachedNote(v) {
      try {
        if (v && v.trim()) localStorage.setItem(NOTE_KEY, v);
        else localStorage.removeItem(NOTE_KEY);
      } catch (_) { /* private mode */ }
    }
    function clearCachedNote() {
      try { localStorage.removeItem(NOTE_KEY); } catch (_) {}
    }

    function familyName() {
      const r = getRegistry() || {};
      return r.registrantFirst || r.name || "the family";
    }

    function splitName(full) {
      const n = (full || "").trim();
      if (!n) return { firstName: "", lastName: "" };
      const i = n.indexOf(" ");
      return i === -1 ? { firstName: n, lastName: "" } : { firstName: n.slice(0, i), lastName: n.slice(i + 1) };
    }

    function build() {
      if (document.querySelector(".rgl-cart-bar")) return;

      // Sticky bottom action bar — only shown when the cart has items. The
      // outer wrapper is pointer-events:none and leaves the bottom-right corner
      // clear so the AI assistant launcher (fixed bottom-right) stays tappable;
      // the inner pill re-enables pointer events.
      const bar = document.createElement("div");
      bar.className = "rgl-cart-bar";
      bar.hidden = true;
      bar.innerHTML = `
        <button type="button" class="rgl-cart-bar__inner" aria-label="Review your gift cart">
          <span class="rgl-cart-bar__left">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
            <span class="rgl-cart-bar__count"></span>
            <span class="rgl-cart-bar__sep" aria-hidden="true">·</span>
            <span class="rgl-cart-bar__total"></span>
          </span>
          <span class="rgl-cart-bar__cta">Review &amp; check out <span aria-hidden="true">→</span></span>
        </button>`;
      bar.querySelector(".rgl-cart-bar__inner").addEventListener("click", open);
      document.body.appendChild(bar);

      const backdrop = document.createElement("div");
      backdrop.className = "rgl-cart-backdrop";
      backdrop.hidden = true;
      backdrop.addEventListener("click", close);
      document.body.appendChild(backdrop);

      const drawer = document.createElement("aside");
      drawer.className = "rgl-cart-drawer";
      drawer.hidden = true;
      drawer.setAttribute("aria-hidden", "true");
      drawer.setAttribute("aria-label", "Your gift cart");
      document.body.appendChild(drawer);

      cart.onChange(() => { onChange(); refresh(); });
    }

    function open() {
      const drawer = document.querySelector(".rgl-cart-drawer");
      const backdrop = document.querySelector(".rgl-cart-backdrop");
      if (!drawer) return;
      renderDrawer(drawer);
      backdrop.hidden = false;
      drawer.hidden = false;
      drawer.setAttribute("aria-hidden", "false");
      requestAnimationFrame(() => { backdrop.classList.add("is-open"); drawer.classList.add("is-open"); });
      document.body.style.overflow = "hidden";
      // Revalidate contributions against live pools (async; re-renders on change).
      refreshContribLines();
    }

    function close() {
      const drawer = document.querySelector(".rgl-cart-drawer");
      const backdrop = document.querySelector(".rgl-cart-backdrop");
      if (!drawer) return;
      drawer.classList.remove("is-open");
      backdrop.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      setTimeout(() => { drawer.hidden = true; backdrop.hidden = true; }, 220);
    }

    function refresh() {
      const bar = document.querySelector(".rgl-cart-bar");
      const count = cart.count();
      if (bar) {
        bar.hidden = count === 0;
        const countEl = bar.querySelector(".rgl-cart-bar__count");
        const totalEl = bar.querySelector(".rgl-cart-bar__total");
        if (countEl) countEl.textContent = `${count} ${count === 1 ? "gift" : "gifts"}`;
        if (totalEl) {
          const entries = cart.items();
          const currency = (entries[0]?.snapshot?.currency) || "USD";
          totalEl.textContent = formatMoney(cart.subtotalCents(), currency);
        }
      }
      const drawer = document.querySelector(".rgl-cart-drawer");
      if (drawer && !drawer.hidden) renderDrawer(drawer);
    }

    function addItem(item) {
      if (!item) return;
      if (cart.has(item.id)) { open(); return; }
      // `price` is the authoritative field (Decimal dollars over JSON);
      // `priceCents` is serialized by the public API. Derive defensively so a
      // missing priceCents can never be read as dollars. Server re-prices.
      const priceCents = item.priceCents != null
        ? item.priceCents
        : Math.round(Number(item.price || 0) * 100) || 0;
      cart.add({
        itemId: item.id,
        quantity: 1,
        snapshot: {
          title: item.title,
          imageUrl: item.imageUrl || "",
          priceCents,
          currency: item.currency || "USD",
          source: item.source,
          externalRetailer: item.externalRetailer || "",
          // Registry-gift free shipping zeros shipping everywhere it's shown
          // (line breakdown + subtotal); the server re-prices authoritatively.
          shippingCents: (getRegistry() || {}).giftFreeShipping ? 0 : (item.shippingCents || 0),
        },
      });
      showToast("Added to your gift cart ✦");
      open();
    }

    /**
     * Add (or update) a group-gift contribution line. `contributionCents` is the
     * amount the buyer puts TOWARD the goal; for external-store pools a 3% fee is
     * added on top at checkout (and previewed in the drawer). The server re-caps
     * the amount against the live pool at checkout, so a stale figure is corrected
     * — not silently charged.
     */
    function addContribution({ item, contributionCents, remainingCents }) {
      if (!item) return;
      const isExt = item.source === "external";
      cart.addContribution({
        itemId: item.id,
        contributionCents,
        snapshot: {
          title: item.title,
          imageUrl: item.imageUrl || "",
          currency: item.currency || "USD",
          source: item.source,
          feeRate: isExt ? EXTERNAL_FEE_RATE : 0,
          externalRetailer: item.externalRetailer || "",
          poolRemainingCents: remainingCents != null ? remainingCents : undefined,
        },
      });
      showToast("Added to your gift cart ✦");
      open();
    }

    // Best-effort: when the drawer opens, re-check each staged contribution
    // against the live pool so amounts are fresh before checkout. Drops lines
    // for pools that closed/funded and clamps amounts down to what's left. This
    // shrinks the window where the server has to bounce a stale contribution.
    async function refreshContribLines() {
      const contribs = cart.items().filter((e) => e.kind === "contribution");
      if (!contribs.length) return;
      for (const e of contribs) {
        try {
          const res = await RegistryAPI.poolSummary(e.itemId);
          const pool = res && res.pool;
          if (!pool) { cart.remove(e.itemId); continue; }
          const remainingCents = Math.max(
            0,
            Math.round(((pool.targetAmount || 0) - (pool.totalContributed || 0)) * 100),
          );
          // Only an "open" pool takes money — funded, or locked by the family
          // (resolving / closed), the server would bounce the line anyway.
          if ((pool.status && pool.status !== "open") || remainingCents <= 0) { cart.remove(e.itemId); continue; }
          const clamped = Math.min(e.contributionCents || 0, remainingCents);
          cart.addContribution({
            itemId: e.itemId,
            contributionCents: clamped,
            snapshot: { poolRemainingCents: remainingCents },
          });
        } catch (err) {
          // 404 = pool disabled/gone — drop it; other errors are transient, keep.
          if (err && err.status === 404) cart.remove(e.itemId);
        }
      }
    }

    function renderDrawer(drawer) {
      const r = getRegistry() || {};
      const entries = cart.items();
      const fam = familyName();

      if (entries.length === 0) {
        drawer.innerHTML = `
          <div class="rgl-cart-drawer__head">
            <h2>Your gift cart</h2>
            <button type="button" class="rgl-cart-drawer__close" aria-label="Close" data-cart-close>&times;</button>
          </div>
          <div class="rgl-cart-empty">
            <p>Your cart is empty.</p>
            <p class="rgl-cart-empty__hint">Add gifts from the registry and check out together.</p>
          </div>`;
        drawer.querySelector("[data-cart-close]")?.addEventListener("click", close);
        return;
      }

      const linesHtml = entries.map((e) => {
        const s = e.snapshot || {};
        const lineTotal = cart.lineTotalCents(e);

        // Group-gift contribution line: editable dollar amount (not a quantity),
        // optional 3% fee preview for external-store pools, and a "still needed"
        // hint when we know the pool's remaining balance.
        if (e.kind === "contribution") {
          const product = e.contributionCents || 0;
          const fee = SERVICE_FEE_ENABLED && s.feeRate ? Math.round(product * s.feeRate) : 0;
          const remaining = s.poolRemainingCents;
          const feeNote = fee
            ? `<div class="rgl-cart-line__breakdown">${formatMoney(product, s.currency)} + ${formatMoney(fee, s.currency)} service fee</div>`
            : "";
          return `
          <div class="rgl-cart-line rgl-cart-line--contrib" data-line="${escapeHtml(e.itemId)}">
            <div class="rgl-cart-line__img">
              ${s.imageUrl ? `<img src="${escapeHtml(s.imageUrl)}" alt="" loading="lazy">` : `<div class="rgl-cart-line__img-ph"></div>`}
            </div>
            <div class="rgl-cart-line__body">
              <div class="rgl-cart-line__title">${escapeHtml(s.title || "Gift")}</div>
              <!-- <div class="rgl-cart-line__tag">Group gift contribution${s.externalRetailer ? ` · ${escapeHtml(s.externalRetailer)}` : ""}</div> -->
              ${feeNote}
              <div class="rgl-cart-line__controls">
                <label class="rgl-cart-contrib">
                  <span class="rgl-cart-contrib__sign">$</span>
                  <input type="number" min="1" step="1" class="rgl-cart-contrib__input"
                    value="${(product / 100).toFixed(2)}" data-contrib-amt="${escapeHtml(e.itemId)}"
                    aria-label="Contribution amount">
                </label>
                <button type="button" class="rgl-cart-line__remove" data-remove="${escapeHtml(e.itemId)}">Remove</button>
              </div>
              ${remaining != null ? `<div class="rgl-cart-line__hint">${formatMoney(remaining, s.currency)} still needed</div>` : ""}
            </div>
            <div class="rgl-cart-line__total">${formatMoney(lineTotal, s.currency)}</div>
          </div>`;
        }

        const isExt = s.source === "external";
        const base = s.priceCents || 0;
        const fee = isExt && SERVICE_FEE_ENABLED ? Math.round(base * EXTERNAL_FEE_RATE) : 0;
        const ship = isExt ? (s.shippingCents || 0) : 0;
        const breakdown = isExt && (fee || ship)
          ? `<div class="rgl-cart-line__breakdown">
               ${formatMoney(base, s.currency)}${fee ? ` + ${formatMoney(fee, s.currency)} service fee` : ""}${ship ? ` + ${formatMoney(ship, s.currency)} shipping` : ""}
             </div>`
          : "";
        return `
          <div class="rgl-cart-line" data-line="${escapeHtml(e.itemId)}">
            <div class="rgl-cart-line__img">
              ${s.imageUrl ? `<img src="${escapeHtml(s.imageUrl)}" alt="" loading="lazy">` : `<div class="rgl-cart-line__img-ph"></div>`}
            </div>
            <div class="rgl-cart-line__body">
              <div class="rgl-cart-line__title">${escapeHtml(s.title || "Gift")}</div>
              ${isExt ? `<div class="rgl-cart-line__tag">From ${escapeHtml(s.externalRetailer || "another store")} · fulfilled by Birch</div>` : ""}
              ${breakdown}
              <div class="rgl-cart-line__controls">
                <div class="rgl-cart-qty">
                  <button type="button" aria-label="Decrease" data-qty-dec="${escapeHtml(e.itemId)}">−</button>
                  <span>${e.quantity || 1}</span>
                  <button type="button" aria-label="Increase" data-qty-inc="${escapeHtml(e.itemId)}">+</button>
                </div>
                <button type="button" class="rgl-cart-line__remove" data-remove="${escapeHtml(e.itemId)}">Remove</button>
              </div>
            </div>
            <div class="rgl-cart-line__total">${formatMoney(lineTotal, s.currency)}</div>
          </div>`;
      }).join("");

      const subtotal = cart.subtotalCents();
      const serviceFee = cart.serviceFeeCents();
      const itemsCents = subtotal - serviceFee; // subtotal already includes the fee
      const currency = (entries[0]?.snapshot?.currency) || "USD";
      const hasExternal = cart.hasExternal();
      // A gift-giver can always choose to ship to themselves (default: the
      // family). The server (checkout-cart) honors "self" on every registry —
      // own-store gifts then ship now instead of being held for the family.
      const canChooseShip = true;
      // Does the family have a usable address on file? (Newer registries can't
      // publish without one, but older ones may lack it.) When missing, ship-to
      // family would fail server-side, so we default to "ship to me" and label
      // the family option as unavailable.
      const pa = r.deliveryAddress || r.shippingAddress || null;
      const hasParentAddress = !!(pa && (pa.line1 || pa.address1) && pa.city && (pa.postalCode || pa.zip));
      const cached = getCachedGifter() || {};

      const prevForm = drawer.querySelector(".rgl-cart-checkout");
      const prev = {
        name: prevForm?.querySelector("[name=name]")?.value,
        email: prevForm?.querySelector("[name=email]")?.value,
        phone: prevForm?.querySelector("[name=phone]")?.value,
        note: prevForm?.querySelector("[name=note]")?.value,
        shipTo: prevForm?.querySelector("[name=shipTo]:checked")?.value,
      };
      const nameVal = prev.name ?? cached.name ?? "";
      const emailVal = prev.email ?? cached.email ?? "";
      const phoneVal = prev.phone ?? "";
      // Gift note survives a pause: prefer the live field (mid-render), else the
      // value persisted to localStorage as the buyer typed (see wiring below).
      const noteVal = prev.note ?? getCachedNote() ?? "";
      const shipSelf = prev.shipTo ? prev.shipTo === "self" : (!!defaultShipSelf() || !hasParentAddress);

      drawer.innerHTML = `
        <div class="rgl-cart-drawer__head">
          <h2>Your gift cart</h2>
          <button type="button" class="rgl-cart-drawer__close" aria-label="Close" data-cart-close>&times;</button>
        </div>
        <div class="rgl-cart-lines">${linesHtml}</div>
        <div class="rgl-cart-summary">
          ${serviceFee > 0 ? `
          <div class="rgl-cart-summary__row rgl-cart-summary__row--minor">
            <span>Items</span>
            <span>${formatMoney(itemsCents, currency)}</span>
          </div>
          <div class="rgl-cart-summary__row rgl-cart-summary__row--minor">
            <span>Service fee (${(EXTERNAL_FEE_RATE * 100).toFixed(0)}%)</span>
            <span>${formatMoney(serviceFee, currency)}</span>
          </div>` : ""}
          <div class="rgl-cart-summary__row">
            <span>Subtotal</span>
            <span>${formatMoney(subtotal, currency)}</span>
          </div>
          <div class="rgl-cart-summary__note">${r.giftFreeShipping ? "Free shipping · taxes" : `Taxes${hasExternal ? "" : " and shipping"}`} calculated at checkout.</div>
          ${serviceFee > 0 ? `<div class="rgl-cart-summary__note rgl-cart-summary__note--fee">
            Items from other stores include a ${(EXTERNAL_FEE_RATE * 100).toFixed(0)}% service fee and are purchased for you by the Birch team.
          </div>` : ""}
        </div>
        <form class="rgl-cart-checkout" novalidate>
          <div class="rgl-cart-field">
            <label for="rgl-cart-name">Your name</label>
            <input id="rgl-cart-name" name="name" required autocomplete="name" value="${escapeHtml(nameVal)}">
          </div>
          <div class="rgl-cart-field">
            <label for="rgl-cart-email">Email</label>
            <input id="rgl-cart-email" name="email" type="email" required autocomplete="email" value="${escapeHtml(emailVal)}">
          </div>
          <div class="rgl-cart-field">
            <label for="rgl-cart-phone">Phone (optional)</label>
            <input id="rgl-cart-phone" name="phone" type="tel" autocomplete="tel" value="${escapeHtml(phoneVal)}">
          </div>
          <div class="rgl-cart-field">
            <label for="rgl-cart-note">Gift note (optional)</label>
            <textarea id="rgl-cart-note" name="note" rows="2">${escapeHtml(noteVal)}</textarea>
          </div>
          ${canChooseShip ? `
          <fieldset class="rgl-cart-ship">
            <legend>Ship to</legend>
            <label${hasParentAddress ? "" : ' class="rgl-cart-ship--disabled"'}><input type="radio" name="shipTo" value="parent" ${shipSelf ? "" : "checked"} ${hasParentAddress ? "" : "disabled"}> ${escapeHtml(fam)}'s address${hasParentAddress ? "" : " (no address on file yet)"}</label>
            <label><input type="radio" name="shipTo" value="self" ${shipSelf ? "checked" : ""}> My address (entered at checkout)</label>
          </fieldset>` : `
          <div class="rgl-cart-ship-fixed">Ships to ${escapeHtml(fam)}'s address.</div>`}
          <button type="submit" class="registry-btn registry-btn--primary rgl-cart-checkout__btn">
            Check out · ${formatMoney(subtotal, currency)}
          </button>
          <p class="rgl-cart-checkout__secure">Secure checkout powered by Shopify.</p>
        </form>`;

      drawer.querySelector("[data-cart-close]")?.addEventListener("click", close);
      drawer.querySelectorAll("[data-qty-inc]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.dataset.qtyInc;
          const e = cart.items().find((x) => x.itemId === id);
          cart.setQty(id, (e?.quantity || 1) + 1);
        }));
      drawer.querySelectorAll("[data-qty-dec]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.dataset.qtyDec;
          const e = cart.items().find((x) => x.itemId === id);
          cart.setQty(id, (e?.quantity || 1) - 1);
        }));
      drawer.querySelectorAll("[data-remove]").forEach((b) =>
        b.addEventListener("click", () => cart.remove(b.dataset.remove)));
      drawer.querySelectorAll("[data-contrib-amt]").forEach((input) =>
        input.addEventListener("change", () => {
          const id = input.dataset.contribAmt;
          const entry = cart.get(id);
          const dollars = parseFloat(input.value);
          if (!Number.isFinite(dollars) || dollars < 1) { refresh(); return; } // revert
          let cents = Math.round(dollars * 100);
          const remaining = entry?.snapshot?.poolRemainingCents;
          if (remaining != null && cents > remaining) cents = remaining; // can't exceed what's left
          cart.setContributionCents(id, cents);
        }));
      drawer.querySelector("[name=note]")?.addEventListener("input", (ev) => saveCachedNote(ev.target.value));
      drawer.querySelector(".rgl-cart-checkout")?.addEventListener("submit", onCompleteCheckout);
    }

    async function onCompleteCheckout(ev) {
      ev.preventDefault();
      const form = ev.currentTarget;
      const data = new FormData(form);
      const fullName = String(data.get("name") || "").trim();
      const email = String(data.get("email") || "").trim();
      if (!fullName || !email) { showToast("Please add your name and email."); return; }

      const { firstName, lastName } = splitName(fullName);
      const phone = String(data.get("phone") || "").trim();
      const note = String(data.get("note") || "").trim();
      const shipTo = String(data.get("shipTo") || (defaultShipSelf() ? "self" : "parent"));
      saveCachedGifter({ name: fullName, email });

      const items = cart.items().map((x) =>
        x.kind === "contribution"
          ? { itemId: x.itemId, quantity: 1, kind: "contribution", contributionCents: x.contributionCents || 0 }
          : { itemId: x.itemId, quantity: x.quantity || 1 });
      if (items.length === 0) { showToast("Your cart is empty."); return; }

      const btn = form.querySelector(".rgl-cart-checkout__btn");
      const label = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="registry-loading" style="width:16px;height:16px;border-width:2px"></span>';

      try {
        const res = await RegistryAPI.checkoutCart({ items, firstName, lastName, email, phone, message: note, giftReceipt: "skip_price", shipTo });
        if (!res?.invoiceUrl) throw new Error("no_invoice");
        cart.clear();
        clearCachedNote();
        location.href = res.invoiceUrl;
      } catch (err) {
        btn.disabled = false;
        btn.innerHTML = label;
        // A pool filled/closed or an amount went stale while staged: reconcile
        // the cart in place (clamp amounts, drop funded items) and let the buyer
        // re-confirm. Nothing was charged.
        if (err?.status === 409 && err?.body?.error === "cart_needs_update" && Array.isArray(err.body.issues)) {
          const msg = applyCartIssues(err.body.issues);
          refresh();
          showToast(msg);
          return;
        }
        showToast(humanizeCartError(err));
      }
    }

    // Reconcile server-reported contribution issues against the local cart.
    // Returns a human message describing what changed.
    function applyCartIssues(issues) {
      let adjusted = 0;
      let removed = 0;
      let paused = 0;
      for (const iss of issues) {
        switch (iss.code) {
          case "amount_adjusted":
            if (iss.newContributionCents > 0) {
              cart.addContribution({
                itemId: iss.itemId,
                contributionCents: iss.newContributionCents,
                snapshot: iss.remainingCents != null ? { poolRemainingCents: iss.remainingCents } : undefined,
              });
              adjusted++;
            } else {
              cart.remove(iss.itemId);
              removed++;
            }
            break;
          case "pool_resolved":
            // poolStatus "resolving" = the family is paying a top-up right
            // now: paused, not settled, so don't call it funded or closed.
            if (iss.poolStatus === "resolving") paused++;
            cart.remove(iss.itemId);
            removed++;
            break;
          case "pool_funded":
          case "pool_closed":
          case "min_contribution":
          case "item_unavailable":
          case "item_under_review":
            cart.remove(iss.itemId);
            removed++;
            break;
          default:
            break;
        }
      }
      if (adjusted && removed) return "Some gifts were updated and some are no longer available — review and check out again.";
      if (removed && removed === paused) return removed === 1
        ? "The family is finishing up a group gift in your cart, so it was removed — check out again."
        : "The family is finishing up some group gifts in your cart, so they were removed — check out again.";
      if (removed) return removed === 1 ? "A gift was fully funded or closed and was removed — check out again." : "Some gifts were fully funded or closed and were removed — check out again.";
      if (adjusted) return "A contribution was adjusted to what's left in the pool — review and check out again.";
      return "Your cart was updated — please review and check out again.";
    }

    function humanizeCartError(err) {
      const code = err?.body?.error || err?.message;
      const title = err?.body?.title ? ` (${err.body.title})` : "";
      switch (code) {
        case "item_under_review": return `An item is still under review${title} and can't be checked out yet.`;
        case "item_unavailable":  return `An item is no longer available${title}. Please remove it and try again.`;
        case "registry_has_no_address": return "This registry has no shipping address yet. Choose “ship to my address,” or ask the family to add theirs.";
        case "item_not_linked":   return `An own-store item${title} can't be checked out. Please ask the family to relink it.`;
        case "pool_funded":       return "This group gift just got fully funded — thank you! Remove it and check out the rest.";
        case "pool_resolved":     return "This group gift isn't open to contributions right now. Please remove it and try again.";
        case "pool_closed":       return "Group contributions are closed for this item. Please remove it and try again.";
        case "contribution_record_failed": return "We couldn't record your contribution. Please try again.";
        case "empty_cart":        return "Your cart is empty.";
        default: return "Checkout couldn't start. Please try again.";
      }
    }

    build();
    refresh();
    return { cart, addItem, addContribution, open, close, refresh };
  }

  // ── AI-build handoff (registry-start → registry-edit) ──────────────────────
  // The "Smart Registry Builder" fork on /pages/registry-start does NOT run the
  // build on its own page any more. A chat build streams over one SSE
  // connection, and that connection dies the instant the tab navigates — so a
  // build started on the intake page was torn down by the very redirect that
  // was supposed to show it happening. The intake page now resolves the
  // registry first, parks the launch payload here, and sends the browser to
  // /pages/registry-edit?id=…&aibuild=1; the editor picks the payload up, opens
  // the chat full screen and starts the build THERE, on the page it will finish
  // on.
  //
  // sessionStorage (not localStorage) because the handoff belongs to this tab's
  // trip and nothing else: a second tab must not inherit a build, and the key
  // must not outlive the browsing session if the redirect never lands.
  const AI_HANDOFF_KEY = "birch:reg:ai_launch";
  // Long enough for a slow redirect + a slow editor boot, short enough that a
  // payload found later is certainly not from this trip.
  const AI_HANDOFF_TTL_MS = 5 * 60 * 1000;

  /**
   * Park a `birch:open-with-query` detail for the editor to dispatch on arrival.
   * Failure is non-fatal (Safari private mode throws): the editor falls back to
   * a generic build off the `?aibuild=1` flag alone.
   * @param {Record<string, any>} detail
   * @param {string|null} registryId
   */
  function writeAiBuildHandoff(detail, registryId) {
    try {
      sessionStorage.setItem(AI_HANDOFF_KEY, JSON.stringify({
        v: 1,
        at: Date.now(),
        registryId: registryId || null,
        detail,
      }));
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Read AND clear the parked payload — consumed exactly once, so a reload of
   * the editor (or a Back into the same URL) can never re-fire a build the
   * family already got.
   * @returns {{ registryId: string|null, detail: Record<string, any>|null }|null}
   */
  function takeAiBuildHandoff() {
    let raw;
    try {
      raw = sessionStorage.getItem(AI_HANDOFF_KEY);
      sessionStorage.removeItem(AI_HANDOFF_KEY);
    } catch (_) { return null; }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== 1 || !parsed.detail) return null;
      if (!(Date.now() - Number(parsed.at) < AI_HANDOFF_TTL_MS)) return null;
      return { registryId: parsed.registryId || null, detail: parsed.detail };
    } catch (_) {
      return null;
    }
  }

  window.BirchRegistry = {
    API: RegistryAPI,
    // Guest mode (see the block at the top of this file).
    isGuest,
    ensureGuestSession,
    claimGuestWork,
    hasGuestPending,
    // Guest token transport (App Proxy strips cookies — see the block above).
    // `withGuestToken` lets other surfaces (the chat companion, the intake page)
    // carry the same identity on their own /apps/* calls.
    getGuestToken,
    withGuestToken,
    // Private-registry unlock token (same transport constraint as above).
    getUnlockToken,
    withUnlockToken,
    // AI-build handoff between the intake page and the editor (see above).
    writeAiBuildHandoff,
    takeAiBuildHandoff,
    signInUrl,
    hasActiveRegistryCached,
    getOrCreateRegistry,
    addBirchItemToCart,
    showToast,
    escapeHtml,
    formatMoney,
    formatDate,
    poolLockNote,
    openGiverModal,
    openContributionAmountModal,
    getCachedGifter,
    saveCachedGifter,
    giftCart,
    giftCartUI,
    EXTERNAL_FEE_RATE,
    classifyStage,
    STAGES,
    PRIORITIES,
  };
})();
